import React from 'react';
import { useStudent } from '../context/StudentContext';
import { Clock, MapPin, User, CheckCircle, Circle, Calendar as CalendarIcon } from 'lucide-react';
import { useMediaQuery } from 'react-responsive';
import './Schedule.css';

const Schedule = () => {
    const { lessons, student, loading, updateLesson } = useStudent();
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

    const handleConfirm = async (lessonId, currentStatus) => {
        const success = await updateLesson(lessonId, { student_confirmed: !currentStatus ? 1 : 0 });
        if (success) {
            // Optional: Add toast notification
        }
    };

    const handleComplete = async (lessonId, currentStatus) => {
        const success = await updateLesson(lessonId, { student_completed: !currentStatus ? 1 : 0 });
        if (success) {
            // Optional: Add toast notification
        }
    };

    if (loading) return null;
    if (!student) return null;

    const sortedLessons = [...lessons].sort((a, b) => {
        if (a.status === 'Upcoming' && b.status !== 'Upcoming') return -1;
        if (a.status !== 'Upcoming' && b.status === 'Upcoming') return 1;
        return new Date(b.date) - new Date(a.date);
    });

    return (
        <div className="page-container schedule-page">
            <header className="page-header flex justify-between items-center">
                <div>
                    <h2 className="page-title">My Schedule</h2>
                    <p className="page-subtitle">Upcoming and completed driving lessons</p>
                </div>
                {!isMobile && (
                    <div className="bg-indigo-500/10 text-indigo-400 p-3 rounded-2xl flex items-center gap-2">
                        <CalendarIcon size={20} />
                        <span className="font-bold text-sm">{sortedLessons.filter(l => l.status === 'Upcoming').length} Upcoming</span>
                    </div>
                )}
            </header>

            <div className="lessons-list">
                {sortedLessons.length === 0 ? (
                    <div className="card text-center py-12">
                        <CalendarIcon size={48} className="mx-auto text-slate-600 mb-4 opacity-20" />
                        <p className="text-slate-400">No lessons scheduled yet.</p>
                    </div>
                ) : (
                    sortedLessons.map(lesson => (
                        <div key={lesson.id} className={`lesson-card ${lesson.status.toLowerCase()} ${isMobile ? 'mobile-card' : ''}`}>
                            <div className="lesson-date-col">
                                <span className="lesson-day">{new Date(lesson.date).getDate()}</span>
                                <span className="lesson-month">{new Date(lesson.date).toLocaleString('default', { month: 'short' })}</span>
                            </div>

                            <div className="lesson-info-col">
                                <div className="lesson-type-row">
                                    <h3 className="lesson-type">{lesson.type}</h3>
                                    <span className={`status-badge ${lesson.status.toLowerCase()}`}>
                                        {(lesson.status === 'Completed' || lesson.studentCompleted) ? <CheckCircle size={14} /> : <Circle size={14} />}
                                        {lesson.studentCompleted ? 'Completed' : lesson.status}
                                    </span>
                                </div>

                                <div className="lesson-details-grid">
                                    <div className="detail-item">
                                        <Clock size={16} />
                                        <span>{new Date(lesson.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                    <div className="detail-item">
                                        <User size={16} />
                                        <span>{lesson.instructor}</span>
                                    </div>
                                    <div className="detail-item">
                                        <MapPin size={16} />
                                        <span>{lesson.location || 'Pick-up: Home'}</span>
                                    </div>
                                </div>

                                {lesson.notes && (
                                    <div className="lesson-notes">
                                        <strong>Notes:</strong> {lesson.notes}
                                    </div>
                                )}

                                <div className="lesson-actions">
                                    <button
                                        onClick={() => handleConfirm(lesson.id, lesson.studentConfirmed)}
                                        className={`action-btn confirm ${lesson.studentConfirmed ? 'active' : ''}`}
                                    >
                                        <CheckCircle size={16} />
                                        {lesson.studentConfirmed ? 'Confirmed' : 'Confirm Lesson'}
                                    </button>
                                    <button
                                        onClick={() => handleComplete(lesson.id, lesson.studentCompleted)}
                                        className={`action-btn complete ${lesson.studentCompleted ? 'active' : ''}`}
                                    >
                                        <CheckCircle size={16} />
                                        {lesson.studentCompleted ? 'Done' : 'Mark Completed'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default Schedule;
